import React, { useCallback, useContext, useMemo, useState } from 'react'
import { Grid, styled, Typography } from '@mui/material'
import { DialogPopup, SimpleSwitch } from '../components'
import { ImageTarget } from '../model'
import { translate } from '../i18n'
import { GTZeroValidator, TimingValidator } from '../validators'
import { ImageTargetFormProps } from './ImageTargetForm.types'
import {
    AnyComponentRef,
    AnyInput,
    AnyUploader,
    useValidatedForm,
} from '../anyform'
import { secure } from '../api'
import { FeedbackContext } from '../feedback'

const placeholder = '/image-target-placeholder.jpg'

const Image = styled('img')(({ theme }) => ({
    maxWidth: '100%',
}))

function ImageTargetForm({
    withWebTarget,
    imageTarget,
    s3Client,
    onUpdated,
    onClose,
}: ImageTargetFormProps) {
    const feedbackContext = useContext(FeedbackContext)
    const { isValid, register, registerInner, bind } =
        useValidatedForm<ImageTarget>()

    const [width, setWidth] = useState(imageTarget?.width ?? 0)
    const [height, setHeight] = useState(imageTarget?.height ?? 0)
    const [imageUrl, setImageUrl] = useState<string>()

    const [width2, setWidth2] = useState(imageTarget?.width2)
    const [height2, setHeight2] = useState(imageTarget?.height2)
    const [imageUrl2, setImageUrl2] = useState<string>()

    const [hasButton, setHashButton] = useState(
        () => imageTarget?.button !== undefined
    )

    const buttonRegister = useCallback(
        (name: string, ref: AnyComponentRef<unknown>) =>
            registerInner('button', name, ref),
        [registerInner]
    )
    const timingValidators = useMemo(() => [new TimingValidator()], [])
    const sizeValidators = useMemo(() => [new GTZeroValidator()], [])

    const hasChangedImage = useCallback(
        (name: string, value?: unknown) => {
            if (value) {
                feedbackContext.showBackdrop()

                secure({
                    key: value as string,
                    http_method: 'GET',
                    seconds: 60,
                })
                    .then((response) => {
                        if (name === 'image') {
                            setImageUrl(response.url)
                        } else {
                            setImageUrl2(response.url)
                        }
                    })
                    .catch((error) => feedbackContext.showError(error.message))
                    .finally(feedbackContext.closeBackdrop)
            } else {
                if (name === 'image') {
                    setImageUrl(undefined)
                } else {
                    setImageUrl2(undefined)
                }
            }
        },
        [feedbackContext]
    )

    const onLoadedImage = useCallback(
        (event: React.SyntheticEvent<any, Event>) => {
            if (imageTarget) return

            const source: string = event.currentTarget?.currentSrc ?? ''
            if (source && !source.endsWith(placeholder)) {
                setWidth(event?.currentTarget?.naturalWidth ?? 0)
                setHeight(event?.currentTarget?.naturalHeight ?? 0)
            }
        },
        [imageTarget]
    )

    const onLoadedImage2 = useCallback(
        (event: React.SyntheticEvent<any, Event>) => {
            const source: string = event.currentTarget?.currentSrc ?? ''
            if (source && !source.endsWith(placeholder)) {
                setWidth2(event?.currentTarget?.naturalWidth)
                setHeight2(event?.currentTarget?.naturalHeight)
            }
        },
        []
    )

    const onUpdate = () => {
        if (!isValid()) return

        const request = bind()
        console.log(request)
        if (hasButton) {
            onUpdated(request)
        } else {
            const { button, ...fixedRequest } = request
            onUpdated(fixedRequest)
        }
    }

    return (
        <DialogPopup
            open
            title={translate(
                `projects.image_targets.popup.${imageTarget ? 'edit' : 'add'}`
            )}
            maxWidth="md"
            fullWidth
            onClose={onClose}
            button={{
                text: translate('buttons.update'),
                onClick: onUpdate,
                disabled: !imageUrl,
            }}
        >
            <Grid container spacing={3}>
                <Grid item xs={12} lg={5}>
                    <Image
                        src={imageUrl || placeholder}
                        alt="target"
                        onLoad={onLoadedImage}
                    />
                </Grid>
                <Grid item xs>
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Grid item xs={12}>
                            <AnyUploader
                                name="image"
                                label={
                                    translate(
                                        'projects.image_targets.image'
                                    ) as string
                                }
                                s3Client={s3Client}
                                initialValue={imageTarget?.image}
                                acceptExtension={'.jpg,.png'}
                                path={'target-images'}
                                required
                                register={register}
                                notifier={hasChangedImage}
                            />
                            <Grid
                                container
                                justifyContent="space-between"
                                alignItems="center"
                                spacing={1}
                            >
                                <Grid item xs={12} md={6}>
                                    <AnyInput
                                        type="number"
                                        name="width"
                                        initialValue={width}
                                        label={
                                            translate(
                                                'projects.image_targets.width'
                                            ) as string
                                        }
                                        required
                                        register={register}
                                        validators={sizeValidators}
                                        disabled
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AnyInput
                                        type="number"
                                        name="height"
                                        initialValue={height}
                                        label={
                                            translate(
                                                'projects.image_targets.height'
                                            ) as string
                                        }
                                        required
                                        register={register}
                                        validators={sizeValidators}
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                            <AnyUploader
                                name="video"
                                label={
                                    translate(
                                        'projects.image_targets.video'
                                    ) as string
                                }
                                s3Client={s3Client}
                                initialValue={imageTarget?.video}
                                acceptExtension={'.mp4,.mov'}
                                path={'videos'}
                                required
                                register={register}
                            />
                        </Grid>

                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <SimpleSwitch
                                checked={hasButton}
                                value=""
                                label={
                                    translate(
                                        'projects.image_targets.button.title'
                                    ) as string
                                }
                                onChanged={(name, checked) =>
                                    setHashButton(checked)
                                }
                                placement="end"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={hasButton ? undefined : { display: 'none' }}
                        >
                            <Grid container>
                                <Grid item xs={12}>
                                    <AnyInput
                                        type="text"
                                        name="text"
                                        initialValue={imageTarget?.button?.text}
                                        label={
                                            translate(
                                                'projects.image_targets.button.text'
                                            ) as string
                                        }
                                        required={hasButton}
                                        disabled={!hasButton}
                                        register={buttonRegister}
                                    />
                                    <AnyInput
                                        type="text"
                                        name="url"
                                        initialValue={imageTarget?.button?.url}
                                        label={
                                            translate(
                                                'projects.image_targets.button.url'
                                            ) as string
                                        }
                                        required={hasButton}
                                        disabled={!hasButton}
                                        register={buttonRegister}
                                    />
                                    <AnyUploader
                                        name="image"
                                        label={
                                            translate(
                                                'projects.image_targets.button.image'
                                            ) as string
                                        }
                                        s3Client={s3Client}
                                        initialValue={
                                            imageTarget?.button?.image
                                        }
                                        acceptExtension={'.jpg,.png'}
                                        path={'target-images'}
                                        disabled={!hasButton}
                                        register={buttonRegister}
                                    />
                                    <AnyInput
                                        type="text"
                                        name="timing"
                                        initialValue={
                                            imageTarget?.button?.timing
                                        }
                                        label={
                                            translate(
                                                'projects.image_targets.button.timing'
                                            ) as string
                                        }
                                        required={hasButton}
                                        disabled={!hasButton}
                                        validators={timingValidators}
                                        register={buttonRegister}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {withWebTarget && (
                    <Grid item xs={12}>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            gutterBottom
                        >
                            {translate(
                                'projects.image_targets.with_web_target'
                            )}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} lg={5}>
                                <Image
                                    src={imageUrl2 || placeholder}
                                    alt="target"
                                    onLoad={onLoadedImage2}
                                />
                            </Grid>
                            <Grid item xs>
                                <Grid
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                >
                                    <Grid item xs={12}>
                                        <AnyUploader
                                            name="image2"
                                            label={
                                                translate(
                                                    'projects.image_targets.image'
                                                ) as string
                                            }
                                            s3Client={s3Client}
                                            initialValue={imageTarget?.image2}
                                            acceptExtension={'.jpg,.png'}
                                            path={'target-images'}
                                            register={register}
                                            notifier={hasChangedImage}
                                        />
                                        <Grid
                                            container
                                            justifyContent="space-between"
                                            alignItems="center"
                                            spacing={1}
                                        >
                                            <Grid item xs={12} md={6}>
                                                <AnyInput
                                                    type="number"
                                                    name="width2"
                                                    initialValue={width2}
                                                    label={
                                                        translate(
                                                            'projects.image_targets.width'
                                                        ) as string
                                                    }
                                                    register={register}
                                                    validators={
                                                        imageUrl2
                                                            ? sizeValidators
                                                            : undefined
                                                    }
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <AnyInput
                                                    type="number"
                                                    name="height2"
                                                    initialValue={height2}
                                                    label={
                                                        translate(
                                                            'projects.image_targets.height'
                                                        ) as string
                                                    }
                                                    register={register}
                                                    validators={
                                                        imageUrl2
                                                            ? sizeValidators
                                                            : undefined
                                                    }
                                                    disabled
                                                />
                                            </Grid>
                                        </Grid>
                                        <AnyUploader
                                            name="video2"
                                            label={
                                                translate(
                                                    'projects.image_targets.video'
                                                ) as string
                                            }
                                            s3Client={s3Client}
                                            initialValue={imageTarget?.video2}
                                            acceptExtension={'.mp4,.mov'}
                                            path={'videos'}
                                            required={!!imageUrl2}
                                            register={register}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </DialogPopup>
    )
}

export default ImageTargetForm
